import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  FileStack,
  Filter,
  Edit2
} from "lucide-react";
import {
  getFeeStructuresByYear,
  getFeeStructuresByYearPortal
} from "../../api/fees/feeApi";
import {
  getAllClasses
} from "../../api/student/studentApi";
import {
  getAllAcademicYears,
  getAllAcademicYearsPortal
} from "../../api/academicyear/academicYearApi";
import FeeStructureModal from "../../components/fees/FeeStructureModal";
import {
  Button,
  Select,
  Card,
  CardContent,
  Table,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const FeeStructureList = ({ isPortal = false }) => {
  const [structures, setStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ academicYearId: "", classId: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const apiFn = isPortal ? getFeeStructuresByYearPortal : getFeeStructuresByYear;
      const params = {};
      if (filters.academicYearId) params.academicYearId = filters.academicYearId;
      if (filters.classId) params.classId = filters.classId;
      const res = await apiFn(params);
      const raw = res?.data || res || [];
      let list = [];
      if (Array.isArray(raw)) {
        list = raw.flatMap((item) => {
          if (item.structures && Array.isArray(item.structures)) {
            return item.structures.map((s) => ({
              ...s,
              classId: s.classId || item.classId,
              className: s.className || item.className,
              academicYear: s.academicYear || item.academicYear,
            }));
          }
          if (item.feeHeads && Array.isArray(item.feeHeads)) {
            return item.feeHeads.map((fh) => ({
              ...fh,
              classId: item.classId,
              className: item.className,
              academicYear: item.academicYear,
            }));
          }
          return [item];
        });
      }
      setStructures(list);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load fee structures.");
      setStructures([]);
    } finally {
      setLoading(false);
    }
  }, [filters, isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        if (!isPortal) {
          const classData = await getAllClasses();
          setClasses(classData || []);
        }
        const ayRes = isPortal
          ? await getAllAcademicYearsPortal()
          : await getAllAcademicYears();
        const years = ayRes?.data || ayRes || [];
        setAcademicYears(Array.isArray(years) ? years : []);
      } catch {
        // non-fatal
      }
    };
    loadMeta();
  }, [isPortal]);

  // Option Derivations
  const yearOptions = useMemo(
    () => [
      { label: "All academic years", value: "" },
      ...academicYears.map((ay) => ({
        label: ay.name || ay.yearLabel || ay.academicYear || ay._id,
        value: ay._id,
      })),
    ],
    [academicYears]
  );

  const classOptions = useMemo(
    () => [
      { label: "All classes", value: "" },
      ...classes.map((cls) => ({
        label: `Class ${cls.className}`,
        value: cls._id,
      })),
    ],
    [classes]
  );

  // Table row pre-processing
  const tableRows = useMemo(
    () =>
      structures.map((item, idx) => ({
        ...item,
        id: item._id || idx,
        classLabel: item.classId?.className
          ? `Class ${item.classId.className}`
          : item.className
          ? `Class ${item.className}`
          : "—",
        feeHeadLabel: item.feeHeadId?.name || item.feeHeadName || item.name || "—",
        amountFormatted: Number(item.amount || 0).toFixed(2),
        dueDateLabel: item.dueDate
          ? new Date(item.dueDate).toLocaleDateString("en-IN")
          : item.dueDay
          ? `Day ${item.dueDay}`
          : "—",
        yearLabel:
          item.academicYear?.name ||
          item.academicYear ||
          item.academicYearId?.name ||
          "—",
      })),
    [structures]
  );

  const columns = [
    {
      key: "classLabel",
      header: "Class",
      render: (item) => (
        <span className="font-medium text-neutral-900 dark:text-neutral-100">{item.classLabel}</span>
      ),
    },
    {
      key: "feeHeadLabel",
      header: "Fee head",
      render: (item) => (
        <span className="text-neutral-800 dark:text-neutral-100">{item.feeHeadLabel}</span>
      ),
    },
    {
      key: "amountFormatted",
      header: "Amount",
      align: "right",
      render: (item) => (
        <span className="font-medium text-neutral-900 dark:text-neutral-100">
          ₹{item.amountFormatted}
        </span>
      ),
    },
    {
      key: "dueDateLabel",
      header: "Due date",
      render: (item) => item.dueDateLabel,
    },
    {
      key: "yearLabel",
      header: "Academic year",
      render: (item) => item.yearLabel,
    },
    ...(!isPortal
      ? [
          {
            key: "actions",
            header: "Actions",
            align: "right",
            nowrap: true,
            render: (item) => (
              <Button
                variant="ghost"
                size="xs"
                leftIcon={<Edit2 className="h-3.5 w-3.5" />}
                onClick={() => {
                  setEditData(item);
                  setModalOpen(true);
                }}
              >
                Edit
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Fee structures</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Class-wise fee configurations by academic year.
          </p>
        </div>
        {!isPortal && (
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setEditData(null);
              setModalOpen(true);
            }}
          >
            Add structure
          </Button>
        )}
      </div>

      {/* Alerts */}
      
      

      {/* Filters Card */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-52">
            <Select
              label="Academic year"
              options={yearOptions}
              value={filters.academicYearId}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, academicYearId: value }))
              }
            />
          </div>
          {!isPortal && (
            <div className="w-full sm:w-44">
              <Select
                label="Class"
                options={classOptions}
                value={filters.classId}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, classId: value }))
                }
              />
            </div>
          )}
          <Button
            variant="secondary"
            leftIcon={<Filter className="h-4 w-4" />}
            onClick={fetchData}
          >
            Apply filters
          </Button>
        </CardContent>
      </Card>

      {/* Data Table */}
      {loading ? (
        <div className="space-y-3 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-card dark:shadow-black/20">
          <div className="flex gap-4">
            <Skeleton height={12} width="20%" />
            <Skeleton height={12} width="25%" />
            <Skeleton height={12} width="15%" />
            <Skeleton height={12} width="20%" />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-4">
              <Skeleton height={16} width="20%" />
              <Skeleton height={16} width="25%" />
              <Skeleton height={16} width="15%" />
              <Skeleton height={16} width="20%" />
            </div>
          ))}
        </div>
      ) : (
        <Table
          columns={columns}
          data={tableRows}
          emptyState={
            <EmptyState
              icon={FileStack}
              title="No fee structures found"
              description="Adjust filters or create a new class-wise fee structure."
              action={
                !isPortal && (
                  <Button
                    variant="primary"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => {
                      setEditData(null);
                      setModalOpen(true);
                    }}
                  >
                    Add structure
                  </Button>
                )
              }
            />
          }
        />
      )}

      {/* Create / Edit Modal */}
      {!isPortal && (
        <FeeStructureModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={() => {
            toast.success(
              editData
                ? "Fee structure updated successfully."
                : "Fee structure created successfully."
            );
            fetchData();
          }}
          editData={editData}
        />
      )}
    </div>
  );
};

export default FeeStructureList;